import { forwardRef, useCallback, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const baseInputClass =
  'flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm shadow-[inset_0_1px_1px_hsl(var(--shadow-color)/0.04)] transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground hover:border-ring/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:bg-muted/40 disabled:opacity-50';

export const Input = forwardRef(({ className, type = 'text', ...props }, ref) => {
  if (type === 'number') {
    return <NumberInput ref={ref} className={className} {...props} />;
  }
  return (
    <input
      ref={ref}
      type={type}
      className={cn(baseInputClass, className)}
      {...props}
    />
  );
});
Input.displayName = 'Input';

// Aceita "1,5" ou "1.5" como entrada decimal. Internamente o input é
// `type="text"` (com `inputMode="decimal"` para teclado numérico em
// mobile) para manter o comportamento consistente entre browsers, em
// vez de delegar ao `type="number"` nativo que ignora a vírgula em
// muitos browsers desktop. O estado externo continua a receber um
// número (com ponto) — apenas a entrada do utilizador é tolerante.
const NUMERIC_PARTIAL_RE = /^-?[\d]*[.,]?[\d]*$/;

const parseLocaleNumber = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return '';
  const normalized = String(raw).replace(',', '.');
  const num = Number(normalized);
  return Number.isFinite(num) ? num : NaN;
};

const NumberInput = forwardRef(
  (
    { className, value, onChange, step = 1, min, max, disabled, name, ...props },
    ref,
  ) => {
    // Preserva visualmente o que o utilizador digita (incluindo a
    // vírgula e estados intermédios como "1," ou "-") até o blur.
    const [draft, setDraft] = useState(null);

    const display = draft ?? (value ?? '');

    const clamp = useCallback(
      (n) => {
        if (Number.isNaN(n) || n === '') return '';
        if (min !== undefined && n < Number(min)) return Number(min);
        if (max !== undefined && n > Number(max)) return Number(max);
        return n;
      },
      [min, max],
    );

    const fire = (newVal) => {
      if (!onChange) return;
      onChange({ target: { value: String(newVal), name, type: 'number' } });
    };

    const handleChange = (e) => {
      const raw = e.target.value;
      // Bloqueia caracteres não numéricos mantendo a edição livre de
      // ponto, vírgula e sinal negativo. Strings vazias passam.
      if (raw !== '' && !NUMERIC_PARTIAL_RE.test(raw)) return;
      setDraft(raw);
      if (raw === '' || raw === '-' || raw === '.' || raw === ',') {
        fire('');
        return;
      }
      const num = parseLocaleNumber(raw);
      if (!Number.isNaN(num) && num !== '') fire(num);
    };

    const handleBlur = () => {
      // Liberta o draft para que o componente volte a refletir o
      // estado canónico (com ponto). Se o último caractere era um
      // separador, garante que enviamos o valor numérico parcial.
      if (draft !== null) {
        const num = parseLocaleNumber(draft);
        if (!Number.isNaN(num)) fire(num);
      }
      setDraft(null);
    };

    const bump = (direction) => {
      const current = Number(parseLocaleNumber(display) || 0);
      const stepNum = Number(step) || 1;
      const next = clamp(current + direction * stepNum);
      setDraft(null);
      fire(next === '' ? '' : next);
    };

    return (
      <div className={cn('relative flex h-9 w-full items-center', className)}>
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={display}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          name={name}
          className={cn(baseInputClass, 'pr-16 tabular-nums')}
          {...props}
        />
        <div className="absolute right-1 top-1 flex gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            tabIndex={-1}
            aria-label="Diminuir"
            disabled={disabled}
            onClick={() => bump(-1)}
            className="h-7 w-7"
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            tabIndex={-1}
            aria-label="Aumentar"
            disabled={disabled}
            onClick={() => bump(1)}
            className="h-7 w-7"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  },
);
NumberInput.displayName = 'NumberInput';
